import { useCallback, useEffect, useRef, useState } from 'react'
import { useOverwatchMapTripAnnotationAudioUrlLazyQuery } from '../AnnotationAudio.hook'
import {
  type AnnotationAudioState,
  PROCESSING_MAX_POLLS,
  PROCESSING_POLL_INTERVAL_MS,
  toAnnotationAudioState,
} from './annotation-audio'

/**
 * Fetches a trip annotation's presigned audio URL on demand and tracks what the
 * player should show.
 *
 * - Nothing is fetched until `play` is called, so listing notes mints no URLs.
 * - `no-cache`: the URL is a bearer credential, so it is never written to the
 *   Apollo cache. It only lives in this hook's state while the player is mounted.
 * - `PROCESSING` is asked for again every {@link PROCESSING_POLL_INTERVAL_MS}
 *   while the player is mounted, up to {@link PROCESSING_MAX_POLLS} times.
 * - `onPlaybackError` refetches once, because an expired URL is the normal way
 *   playback fails. A second failure is shown as an error with a retry.
 */
export const useTripAnnotationAudio = (annotationId: string) => {
  const [state, setState] = useState<AnnotationAudioState>({ status: 'idle' })
  const [fetchAudio] = useOverwatchMapTripAnnotationAudioUrlLazyQuery({
    fetchPolicy: 'no-cache',
  })
  const mountedRef = useRef(true)
  const pollsRef = useRef(0)
  const refetchedAfterErrorRef = useRef(false)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const resolve = useCallback(async () => {
    try {
      const { data, error } = await fetchAudio({ variables: { annotationId } })
      if (!mountedRef.current) return
      const audio = data?.tripAnnotationAudioUrl
      setState(
        error || !audio
          ? { status: 'error' }
          : toAnnotationAudioState(
              audio,
              pollsRef.current >= PROCESSING_MAX_POLLS,
            ),
      )
    } catch {
      if (mountedRef.current) setState({ status: 'error' })
    }
  }, [annotationId, fetchAudio])

  // Keyed on `state` itself, not a derived boolean: each PROCESSING answer is a
  // new state object, which schedules the next poll.
  useEffect(() => {
    if (state.status !== 'processing' || state.exhausted) return
    const timeout = setTimeout(() => {
      pollsRef.current += 1
      void resolve()
    }, PROCESSING_POLL_INTERVAL_MS)
    return () => clearTimeout(timeout)
  }, [state, resolve])

  const play = useCallback(() => {
    pollsRef.current = 0
    refetchedAfterErrorRef.current = false
    setState({ status: 'loading' })
    void resolve()
  }, [resolve])

  const onPlaybackError = useCallback(() => {
    if (refetchedAfterErrorRef.current) {
      setState({ status: 'error' })
      return
    }
    refetchedAfterErrorRef.current = true
    setState({ status: 'loading' })
    void resolve()
  }, [resolve])

  return { state, play, onPlaybackError }
}
